/* import __COLOCATED_TEMPLATE__ from './side-conversation-panel.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */

import Component from '@glimmer/component';
// @ts-ignore
import { trackedReset } from 'tracked-toolbox';
import { action } from '@ember/object';
// @ts-ignore
import { ref } from 'ember-ref-bucket';
import { inject as service } from '@ember/service';
import type InboxState from 'embercom/services/inbox-state';
import { ComposerLocation } from 'embercom/services/inbox-state';
import type Conversation from 'embercom/objects/inbox/conversation';
import type Session from 'embercom/services/session';
import type ConversationStreamSettings from 'embercom/services/conversation-stream-settings';
import { ComposerPaneType } from 'embercom/objects/inbox/composer-pane';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import type CommandKService from 'embercom/services/command-k';
import { DisplayContext } from 'embercom/services/command-k';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import type IntlService from 'embercom/services/intl';
import type Router from '@ember/routing/router-service';
import type Nexus from 'embercom/services/nexus';
import { type EmberKeyboardEvent, EscKeyPriority } from 'embercom/lib/inbox2/types';
import type EmojiService from 'embercom/services/emoji-service';
import type InboxApi from 'embercom/services/inbox-api';
import { SideConversationResource } from './side-conversation-resource';
import { registerDestructor } from '@ember/destroyable';
import { toggleMessengerVisibility } from 'embercom/lib/intercom-widget-helper';
import PdfExportResource from './pdf-export-resource';
import FormForDownload from 'embercom/lib/form-for-download';
import { downloadFromResponse } from 'embercom/lib/download-from-response';
import { postRequest } from 'embercom/lib/inbox/requests';
import {
  canExportConversationTranscriptsInbox2,
  exportConversationTranscriptsPermission,
} from 'embercom/lib/settings/permissions/inline-permission-enforcement';

interface Args {
  sideConversationId: number;
  parentConversationId: number;
  shouldFocusComposer?: boolean;
  resetShouldFocusComposer?: () => void;
  isNotePaneActive: boolean;
  onBack?: () => void;
}

interface Signature {
  Args: Args;
}

type FocusFunction = () => void;
export default class SideConversationPanel extends Component<Signature> {
  @service declare session: Session;
  @service declare inboxApi: InboxApi;
  @service declare inboxState: InboxState;
  @service declare conversationStreamSettings: ConversationStreamSettings;
  @service declare commandK: CommandKService;
  @service declare intercomEventService: any;
  @service declare permissionsService: any;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare intl: IntlService;
  @service declare router: Router;
  @service declare nexus: Nexus;
  @service declare emojiService: EmojiService;

  @tracked composerFocusFn?: FocusFunction;
  @tracked canShowSendAndCloseHint = false;

  @trackedReset({
    memo: 'args.shouldFocusComposer',
    update(component: SideConversationPanel, _: string, last: boolean) {
      if (last) {
        return last;
      }

      return component.args.shouldFocusComposer;
    },
  })
  isComposerVisible = this.args.shouldFocusComposer ?? false;

  @ref('preview-panel-composer-container') declare composerContainer: HTMLElement;
  @ref('preview-panel-composer-opener') declare composerOpener: HTMLElement;

  readonly replyPane = ComposerPaneType.Reply;
  readonly ComposerLocation = ComposerLocation;
  readonly keyboardPriority = 1;

  sideConversationResource = SideConversationResource.from(this, () => ({
    sideConversationId: this.args.sideConversationId,
    parentConversationId: this.args.parentConversationId,
  }));

  pdfExportResource = PdfExportResource.from(this, () => ({
    conversationId: this.args.sideConversationId,
  }));

  constructor(owner: SideConversationPanel, args: Args) {
    super(owner, args);
    this.setupMessengerVisibility();
    this.commandK.setActivationContext(DisplayContext.SideConversation);
  }

  willDestroy() {
    super.willDestroy();
    this.inboxState.commandKSourceConversationId = undefined;
    this.commandK.resetActivationContext();
  }

  get activePane(): ComposerPaneType {
    return this.replyPane;
  }

  get conversation(): Conversation {
    return this.sideConversationResource.sideConversation;
  }

  get escPriority() {
    return EscKeyPriority.conversationPanel;
  }

  get isSlackChannel(): boolean {
    return this.conversation?.linkedChannel?.channel_type === 'slack_channel';
  }

  get channelIcon(): 'slack' | 'email' {
    return this.isSlackChannel ? 'slack' : 'email';
  }

  get channelDisplayName(): string {
    return this.conversation?.linkedChannel?.channel_name ?? '';
  }

  get externalChannelIcon(): 'locked' | 'hash' {
    if (this.conversation?.linkedChannel?.private_channel) {
      return 'locked';
    }
    return 'hash';
  }

  get firstParticipantName(): string {
    return this.conversation.firstParticipant?.email ?? '';
  }

  get exportOptions() {
    return [
      {
        id: 'export-text',
        text: this.intl.t('inbox.conversation-header.export-conversation-text'),
        icon: 'export',
      },
      {
        id: 'export-pdf',
        text: this.intl.t('inbox.conversation-header.export-conversation-pdf'),
        icon: 'export',
      },
    ];
  }

  @action onExportOptionSelected(option: { id: string }) {
    if (option.id === 'export-text') {
      this.exportConversation();
    } else if (option.id === 'export-pdf') {
      this.exportConversationPdf();
    }
  }

  get canExportSideConversation(): boolean {
    return canExportConversationTranscriptsInbox2(this.session);
  }

  @action async exportConversation() {
    if (!canExportConversationTranscriptsInbox2(this.session)) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        exportConversationTranscriptsPermission,
      );
      return;
    }

    let url = `/ember/inbox/side_conversations/${this.args.sideConversationId}/download_transcript?v=${Math.floor(
      Math.random() * 1e8,
    )}&user_visible_only=true`;

    if (this.session.workspace.isFeatureEnabled('spg-inbox-fetch-based-transcript-download')) {
      try {
        let response = await postRequest(url, {
          app_id: this.session.workspace.id,
          parent_conversation_id: this.args.parentConversationId,
        });

        await downloadFromResponse(response, 'transcript.txt');
      } catch (error) {
        console.error('Failed to export side conversation:', error);
      }
    } else {
      FormForDownload.postNewTab(url, {
        app_id: this.session.workspace.id,
        parent_conversation_id: this.args.parentConversationId,
      });
    }
  }

  @action async exportConversationPdf() {
    if (!canExportConversationTranscriptsInbox2(this.session)) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        exportConversationTranscriptsPermission,
      );
      return;
    }

    let url = `/ember/inbox/side_conversations/${this.args.sideConversationId}/download_pdf?v=${Math.floor(
      Math.random() * 1e8,
    )}&user_visible_only=true`;

    try {
      let response = await postRequest(url, {
        app_id: this.session.workspace.id,
        parent_conversation_id: this.args.parentConversationId,
      });

      let json = (await response.json()) as { request_id: string };
      this.pdfExportResource.startExport(json.request_id);
    } catch (error: any) {
      this.pdfExportResource.closeErrorModal();

      if (error.status !== 403 && error.status !== 404) {
        console.error('Failed to trigger PDF export:', error);
      }
    }
  }

  @action onReady({ focus }: { focus: FocusFunction }) {
    this.composerFocusFn = focus;

    if (this.args.shouldFocusComposer) {
      this.inboxState.commandKSourceConversationId = this.conversation.id;
      next(() => focus());
      this.args.resetShouldFocusComposer?.();
    }
  }

  @action showComposer() {
    this.isComposerVisible = true;
    toggleMessengerVisibility({ hide: true });
    this.inboxState.setActiveComposer(ComposerLocation.ConversationPreviewPanel);
    this.inboxState.commandKSourceConversationId = this.conversation.id;
    next(() => this.composerFocusFn?.());
  }

  @action hideComposer(event?: Event, kev?: EmberKeyboardEvent) {
    event?.stopPropagation();
    kev?.stopPropagation();
    kev?.stopImmediatePropagation();

    this.isComposerVisible = false;
    toggleMessengerVisibility({ hide: false });
    this.inboxState.setActiveComposer(ComposerLocation.ConversationPage);
    this.inboxState.commandKSourceConversationId = undefined;
    setTimeout(() => this.composerOpener.focus(), 20);
  }

  @action updateActivationContext() {
    if (
      this.commandK.currentActivationContext !== DisplayContext.SideConversation &&
      this.commandK.currentActivationContext !== DisplayContext.SideConversationMessageActions
    ) {
      this.commandK.setActivationContext(DisplayContext.SideConversation);
    }
  }

  private setupMessengerVisibility() {
    toggleMessengerVisibility({ hide: true });

    registerDestructor(this, () => {
      toggleMessengerVisibility({ hide: false });
    });
  }

  @action trackSidebarToggle(options = { keyboardShortcutUsed: false }) {
    this.intercomEventService.trackAnalyticsEvent({
      action: this.inboxSidebarService.isSidebarCollapsed ? 'hidden' : 'shown',
      object: 'conversation_panel',
      section: options.keyboardShortcutUsed ? 'shortcuts' : undefined,
      shortcut_key: options.keyboardShortcutUsed,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::SideConversationPanel': typeof SideConversationPanel;
    'inbox2/side-conversation-panel': typeof SideConversationPanel;
  }
}
